<template>
  <div class="">
    <!-- Pathway Title and Image -->
    <v-row no-gutters style="min-height: 160px">
      <!-- Image Column -->
      <v-col cols="12" sm="4" md="4" lg="4" xl="4" class="d-flex align-stretch">
        <v-img :src="pathwayImageUrl" alt="Pathway Image" class="rounded w-100" cover style="min-height: 140px" />
      </v-col>

      <!-- Content Column -->
      <v-col cols="12" sm="8" md="8" lg="8" xl="8" class="ps-sm-4 pt-3 pt-sm-0">
        <!-- Card Header with REGISTERED badge and menu icon -->

        <v-card-title class="d-flex justify-space-between align-center pa-0">
          <!-- Registered Card -->
          <div class="d-flex align-center">
            <span
              class="text-caption font-weight-bold mr-3"
              :style="{
                backgroundColor: isPathwayComplete ? '#e8f5e9' : '#ffeebe',
                color: isPathwayComplete ? '#2e7d32' : '#000',
                letterSpacing: '0.5px',
                padding: '3px 16px',
                borderRadius: '4px',
                display: 'inline-block'
              }"
            >
              {{ isPathwayComplete ? 'COMPLETED' : 'REGISTERED' }}
            </span>
          </div>

          <!-- Menu with Remove Option -->
          <v-menu location="bottom end">
            <template v-slot:activator="{ props }">
              <v-btn
                style="margin-right: 0 !important"
                icon
                variant="text"
                size="x-small"
                class="mobile-touch-target-icon"
                v-bind="props"
              >
                <v-icon size="large" color="#747474">mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-card class="rounded-lg" elevation="0" style="border: 1px solid #bfbfbf">
              <v-list density="compact">
                <v-list-item @click="handleViewPathwayDetails">
                  <v-list-item-title class="text-body-2"> View details </v-list-item-title>
                </v-list-item>
                <v-list-item @click="handleExplorePathways">
                  <v-list-item-title class="text-body-2"> Explore more pathways </v-list-item-title>
                </v-list-item>
                <v-list-item @click="showUnenrollDialog = true">
                  <v-list-item-title class="text-body-2"> Remove pathway </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </v-card-title>

        <!-- Pathway Title -->
        <h3 class="font-weight-bold mb-1 mt-4 mt-md-0" style="font-size: 20px; line-height: 1.3">
          Pathway to {{ fullPathwayName }}
        </h3>

        <!-- Credit Progress Bar -->
        <div class="">
          <ProgressBar
            :transcripted="creditProgressData.transcripted"
            :waiting="creditProgressData.waiting"
            :in-progress="creditProgressData.inProgress"
            :not-started="creditProgressData.notStarted"
            :total-credits="creditProgressData.totalCredits"
            :show-legend="false"
          />
        </div>
      </v-col>
    </v-row>

    <!-- GPA Requirement Banner -->
    <PathwayGPABanner :gpa="pathwayGPA" :min-g-p-a="pathwayMinGPA" :is-cumulative="pathwayIsCumulative" class="mt-4" />

    <!-- Divider and Courses Section (hidden when showHeaderOnly is true) -->
    <transition name="courses-expand">
      <div v-if="!showHeaderOnly" class="courses-section">
        <!-- Divider -->
        <v-divider class="my-4" style="border-color: #bfbfbf; opacity: 1" />

        <!-- Required Courses Section -->
        <div v-if="enrichedRequiredCourses.length > 0" class="mb-6">
          <h4 class="font-weight-bold mb-3" style="font-size: 18px">Required courses</h4>

          <div class="d-flex flex-column gap-3">
            <template v-for="(courseGroup, groupIndex) in displayedRequiredCourses" :key="'req-' + groupIndex">
              <!-- Single course (AND requirement) -->
              <CourseCardCompact
                v-if="courseGroup.length === 1"
                :course="courseGroup[0]"
                :is-last-course="true"
                :show-details-button="!courseGroup[0]._isDesignation && courseGroup[0].ui?.showDropdown"
                :dropdown-open="openDropdownCoursePrefix === courseGroup[0].coursePrefix"
                @add-course="handleAddCourse"
                @button-action="handleButtonAction"
                @dropdown-toggle="handleDropdownToggle(courseGroup[0].coursePrefix, $event)"
              />

              <!-- Multiple courses (OR requirement) - collapsible -->
              <CourseOrGroup v-else :courses="courseGroup">
                <CourseCardCompact
                  v-for="(course, courseIndex) in courseGroup"
                  :key="course.coursePrefix"
                  :course="course"
                  :is-last-course="courseIndex === courseGroup.length - 1"
                  :show-details-button="!course._isDesignation && course.ui?.showDropdown"
                  :dropdown-open="openDropdownCoursePrefix === course.coursePrefix"
                  @add-course="handleAddCourse"
                  @button-action="handleButtonAction"
                  @dropdown-toggle="handleDropdownToggle(course.coursePrefix, $event)"
                />
              </CourseOrGroup>
            </template>
          </div>

          <!-- View All/Less Toggle -->
          <div v-if="enrichedRequiredCourses.length > coursesToDisplay" class="text-center mt-3">
            <a
              href="#"
              class="text-decoration-underline font-weight-medium"
              style="color: #8c1d40"
              @click.prevent="toggleShowAllRequired"
            >
              {{ showAllRequiredCourses ? 'View less' : 'View all required courses' }}
            </a>
          </div>
        </div>

        <!-- Recommended Courses Section -->
        <div class="mb-2">
          <h4 class="font-weight-bold mb-3" style="font-size: 18px">Recommended courses</h4>

          <div v-if="enrichedRecommendedCourses.length > 0" class="d-flex flex-column gap-3">
            <template v-for="(courseGroup, groupIndex) in displayedRecommendedCourses" :key="'rec-' + groupIndex">
              <!-- Single course (AND requirement) -->
              <CourseCardCompact
                v-if="courseGroup.length === 1"
                :course="courseGroup[0]"
                :is-last-course="true"
                :show-details-button="!courseGroup[0]._isDesignation && courseGroup[0].ui?.showDropdown"
                :dropdown-open="openDropdownCoursePrefix === courseGroup[0].coursePrefix"
                @add-course="handleAddCourse"
                @button-action="handleButtonAction"
                @dropdown-toggle="handleDropdownToggle(courseGroup[0].coursePrefix, $event)"
              />

              <!-- Multiple courses (OR requirement) - collapsible -->
              <CourseOrGroup v-else :courses="courseGroup" default-label="RECOMMENDED">
                <CourseCardCompact
                  v-for="(course, courseIndex) in courseGroup"
                  :key="course.coursePrefix"
                  :course="course"
                  :is-last-course="courseIndex === courseGroup.length - 1"
                  :show-details-button="!course._isDesignation && course.ui?.showDropdown"
                  :dropdown-open="openDropdownCoursePrefix === course.coursePrefix"
                  @add-course="handleAddCourse"
                  @button-action="handleButtonAction"
                  @dropdown-toggle="handleDropdownToggle(course.coursePrefix, $event)"
                />
              </CourseOrGroup>
            </template>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-4" style="color: #666666">
            <p class="text-body-2 mb-0">No recommended courses available at this time.</p>
          </div>

          <!-- View All/Less Toggle -->
          <div v-if="enrichedRecommendedCourses.length > coursesToDisplay" class="text-center mt-3">
            <a
              href="#"
              class="text-decoration-underline font-weight-medium"
              style="color: #8c1d40"
              @click.prevent="toggleShowAllRecommended"
            >
              {{ showAllCourses ? 'View less' : 'View all recommended courses' }}
            </a>
          </div>
        </div>
      </div>
    </transition>

    <!-- Remove Confirmation Dialog -->
    <v-dialog v-model="showUnenrollDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold d-flex justify-space-between align-center pa-4">
          Confirm pathway removal
          <v-btn
            style="margin-right: 0 !important"
            icon
            variant="text"
            @click="showUnenrollDialog = false"
            aria-label="Close dialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <p class="text-body-1 mb-3">
            You are about to remove the <strong>"{{ pathwayName }}"</strong> pathway. Are you sure you want to do this?
          </p>
          <p class="text-body-2 mb-0" style="color: #666666">
            You'll still have full access to your ULC dashboard and any existing courses.
          </p>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0" :class="[$vuetify.display.smAndDown ? 'd-flex flex-column gap-2' : '']">
          <v-spacer v-if="!$vuetify.display.smAndDown" />
          <v-btn
            variant="outlined"
            rounded
            class="text-none font-weight-bold px-4"
            style="border-color: #2a2a2a; margin-right: 0 !important"
            :class="[$vuetify.display.smAndDown ? 'w-100' : '']"
            @click="showUnenrollDialog = false"
          >
            No, go back
          </v-btn>
          <v-btn
            variant="flat"
            rounded
            color="#ffc627"
            class="text-none font-weight-bold px-4"
            style="color: #000000; margin-right: 0 !important"
            :class="[$vuetify.display.smAndDown ? 'w-100' : '']"
            :loading="isUnenrolling"
            @click="handleUnenroll"
          >
            Yes, remove pathway
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Pathway Details Sidebar -->
    <PathwayDetailsSidebar
      v-model="showPathwayDetails"
      :pathway-id="pathwayId"
      :pathway-data="myPathwayData"
      :is-enrolled="true"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useUserStore, usePathwaysStore, useUiStore } from '@/stores';
import { resolveOfferingCanvasCourseId } from '@/utils/ga4Items';
import { useCheckoutStore } from '@/stores/checkout';
import { Logger } from '@/mixins/logger';
import { DataLayerMixin } from '@/mixins/dataLayerMixin';
import { DEFAULT_PATHWAY_IMAGE } from '@/constants/pathways';
import ProgressBar from '@/components/ui/ProgressBar.vue';
import PathwayGPABanner from './PathwayGPABanner.vue';
import PathwayDetailsSidebar from './PathwayDetailsSidebar.vue';
import CourseCardCompact from '@/components/courses/CourseCardCompact.vue';
import CourseOrGroup from '@/components/courses/CourseOrGroup.vue';
import { ENROLLMENT_TYPE_LABELS, PREPAID_ENROLLMENT_BENEFIT_TYPES } from '@/constants/checkout';

export default defineComponent({
  name: 'EnrolledPathwayCard',

  mixins: [DataLayerMixin],

  components: {
    ProgressBar,
    PathwayGPABanner,
    PathwayDetailsSidebar,
    CourseCardCompact,
    CourseOrGroup
  },

  props: {
    pathwayTitle: {
      type: String,
      default: ''
    },
    pathwayImage: {
      type: String,
      default: ''
    },
    coursesToDisplay: {
      type: Number,
      default: 3
    },
    showHeaderOnly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showPathwayDetails: false,
      showUnenrollDialog: false,
      isUnenrolling: false,
      showAllCourses: false,
      showAllRequiredCourses: false,
      openDropdownCoursePrefix: null
    };
  },

  computed: {
    pathwaysStore() {
      return usePathwaysStore();
    },
    uiStore() {
      return useUiStore();
    },
    myPathwayData() {
      return this.pathwaysStore.myPathway;
    },
    isPathwayComplete() {
      return this.myPathwayData?.pathwayComplete === true;
    },
    pathwayName() {
      return this.myPathwayData?.pathwayName || '';
    },
    pathwayId() {
      return this.myPathwayData?.pathwayId || null;
    },
    fullPathwayName() {
      return this.myPathwayData?.pathwayName || '';
    },
    pathwayImageUrl() {
      return this.myPathwayData?.imageUrl || DEFAULT_PATHWAY_IMAGE;
    },
    creditsNeeded() {
      return this.myPathwayData?.creditsNeeded || this.myPathwayData?.credits || 24;
    },
    pathwayGPA() {
      return this.myPathwayData?.gpa || null;
    },
    pathwayMinGPA() {
      return parseFloat(this.myPathwayData?.minGPA) || 0;
    },
    pathwayIsCumulative() {
      return Boolean(this.myPathwayData?.isCumulative);
    },

    // Enriched courses from backend (pre-computed badges, buttons, offerings).
    // Each entry is a group: one card normally, several for an "A OR B" requirement.
    enrichedRequiredCourses() {
      return this.toCourseGroups(this.myPathwayData?.enriched_courses?.required);
    },
    enrichedRecommendedCourses() {
      return this.toCourseGroups(this.myPathwayData?.enriched_courses?.recommended);
    },
    displayedRequiredCourses() {
      return this.showAllRequiredCourses
        ? this.enrichedRequiredCourses
        : this.enrichedRequiredCourses.slice(0, this.coursesToDisplay);
    },
    displayedRecommendedCourses() {
      return this.showAllCourses
        ? this.enrichedRecommendedCourses
        : this.enrichedRecommendedCourses.slice(0, this.coursesToDisplay);
    },

    // Credit progress data for progress bar
    creditProgressData() {
      const creditCompletion = this.myPathwayData?.creditCompletion;
      const overall = creditCompletion?.overall;
      const required = creditCompletion?.required;
      const totalCredits = overall?.creditsNeeded || this.creditsNeeded;

      if (!overall) {
        return {
          transcripted: 0,
          waiting: 0,
          inProgress: 0,
          notStarted: totalCredits,
          totalCredits
        };
      }

      let completed = overall.completed || 0;
      let inProgress = overall.inProgress || 0;

      // If there are required credits, they act as a hard limiter
      if (required && required.total > 0) {
        const missingRequired = Math.max(0, required.total - required.completed);
        const maxAchievable = totalCredits - missingRequired;

        if (completed >= maxAchievable) {
          completed = maxAchievable;
          inProgress = 0;
        } else if (completed + inProgress > maxAchievable) {
          inProgress = maxAchievable - completed;
        }
      }

      // Cap at totalCredits
      if (completed >= totalCredits) {
        completed = totalCredits;
        inProgress = 0;
      } else if (completed + inProgress > totalCredits) {
        inProgress = totalCredits - completed;
      }

      const notStarted = Math.max(0, totalCredits - completed - inProgress);

      return {
        transcripted: 0,
        waiting: completed,
        inProgress,
        notStarted,
        totalCredits
      };
    }
  },

  methods: {
    // Tolerates the pre-grouping backend shape (a flat list of cards)
    toCourseGroups(courses) {
      return (courses || []).map((entry) => (Array.isArray(entry) ? entry : [entry]));
    },

    handleDropdownToggle(coursePrefix, isOpen) {
      if (isOpen) {
        this.openDropdownCoursePrefix = coursePrefix;
      } else if (this.openDropdownCoursePrefix === coursePrefix) {
        // Only clear if this card is still the active one (avoid race with another card opening)
        this.openDropdownCoursePrefix = null;
      }
    },

    handleAddCourse(eventData) {
      const checkoutStore = useCheckoutStore();
      const course = eventData.course;
      const selectedOfferingId = String(eventData.courseOfferingId || '');
      const selectedOffering = course.offerings?.find((o) => String(o.offeringId) === selectedOfferingId);
      const openEdxCourseId =
        selectedOffering?.openEdxCourseId || course.offering?.openEdxCourseId || course.lms?.openEdxCourseId || '';
      const payment = course.payment || {};
      const benefitType = String(useUserStore().apis?.learnerData?._benefitType || '');
      const multiEnrollmentType = ENROLLMENT_TYPE_LABELS[payment.enrollmentType] || '';
      // Mirrors the checkout's own prepaid gate (isPrepaidCourseAmount) so the card never
      // claims a prepaid checkout the checkout will refuse; a LearnerPay course can be
      // benefitsEligible with a real price, so an explicit $0 is required too.
      const isPrepaid =
        payment.benefitsEligible === true &&
        payment.creditEligibilityPrice === 0 &&
        PREPAID_ENROLLMENT_BENEFIT_TYPES.includes(benefitType) &&
        multiEnrollmentType !== 'LearnerPay';

      checkoutStore.setCheckoutData({
        title: course.courseName || '',
        coursePrefix: course.coursePrefix || '',
        coursePrefixDisplayLabel: course.coursePrefixDisplayLabel || course.coursePrefix || '',
        courseStartDate: eventData.courseStartDate || '',
        courseEndDate: eventData.courseEndDate || selectedOffering?.endDate || course.offering?.endDate || '',
        courseType: course.courseType || '',
        credits: course.credits || 0,
        courseOfferingId: selectedOfferingId,
        courseOfferingName: openEdxCourseId,
        sessionName: selectedOffering?.sessionName || '',
        enrollmentId: '0',
        enrollmentType: 'new',
        buttonLabel: 'Access Course',
        // A covered course checks out at $0, where the Stripe form never mounts — only the
        // prepaid path ('Enroll and pay' + a covered benefit type) completes the enrollment.
        buttonAction: isPrepaid ? 'Enroll and pay' : 'Access Course',
        creditEligibilityPrice: payment.creditEligibilityPrice || 0,
        benefitsEligible: payment.benefitsEligible || false,
        benefitType,
        multiEnrollmentType,
        canvasEnrollmentId: 0,
        canvasUserId: course.lms?.canvasUserId || null,
        canvasCourseId: resolveOfferingCanvasCourseId(selectedOffering || course.offering, course.lms),
        from: 'pathways',
        courseInfo: { _openEdxCourseId: openEdxCourseId }
      });

      this.$router.push({ name: 'courseCheckout' });
    },

    handleButtonAction(payload) {
      // buildActionPayload only sends { coursePrefix, courseId, action, ... }
      // Look up the full course object from enriched lists to check _isDesignation
      const prefix = payload.coursePrefix || payload.course?.coursePrefix;
      const allCourses = [...this.enrichedRequiredCourses, ...this.enrichedRecommendedCourses].flat();
      const course = allCourses.find((c) => c.coursePrefix === prefix) || payload;

      // Designation pseudo-cards with "Search courses" button
      if (course._isDesignation && course._designationCode) {
        this.$router.push({ name: 'addCourses', query: { searchDesignation: course._designationCode } });
      }
      // Non-designation actions (Access Course, Enroll and pay, View Transcript, etc.)
      // are fully handled inside CourseCardCompact via its actionType switch — no parent handling needed.
    },

    toggleShowAllRecommended() {
      this.asuoDataLayerClick('EnrolledPathwayCard', '2');
      this.showAllCourses = !this.showAllCourses;
    },

    toggleShowAllRequired() {
      this.asuoDataLayerClick('EnrolledPathwayCard', '3');
      this.showAllRequiredCourses = !this.showAllRequiredCourses;
    },

    handleViewPathwayDetails() {
      this.asuoDataLayerClick('EnrolledPathwayCard', '1');
      this.showPathwayDetails = true;
    },

    handleExplorePathways() {
      this.$router.push({ path: '/pathways', query: { scrollTo: 'degrees' } });
    },

    async handleUnenroll() {
      if (!this.pathwayId) {
        Logger.methods.logError('EnrolledPathwayCard', 'No pathway ID found for unenrollment');
        return;
      }

      this.asuoDataLayerClick('EnrolledPathwayCard', '0');

      const pathwayName = this.fullPathwayName || this.pathwayName || 'this pathway';

      try {
        this.isUnenrolling = true;

        const data = {
          pathwayId: String(this.pathwayId),
          isInactive: true
        };

        const contactId = this.$conn.learnerData?._contactId;
        const identifier = contactId ? { contactId } : null;

        if (!identifier) {
          throw new Error('User identifier (contactId or asuRite) not found');
        }

        await this.$conn.updatePathwayConnection(identifier, data);
        this.showUnenrollDialog = false;

        this.uiStore.showSuccessNotification(`Pathway to ${pathwayName}`, 'has been removed from your dashboard.');

        this.$emit('pathway-unenrolled');
      } catch (error) {
        Logger.methods.logError('EnrolledPathwayCard', 'Failed to remove pathway: ' + error);
        this.uiStore.showErrorNotification('Removal Failed', 'Failed to remove pathway. Please try again.');
      } finally {
        this.isUnenrolling = false;
      }
    }
  }
});
</script>

<style scoped>
.gap-3 {
  gap: 12px;
}

/* Smooth transition for courses section */
.courses-expand-enter-active,
.courses-expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.courses-expand-enter-from,
.courses-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.courses-expand-enter-to,
.courses-expand-leave-from {
  max-height: 5000px;
  opacity: 1;
}
</style>

<style>
/* Unscoped to target v-list-item in v-menu dropdown */
.v-list-item:hover {
  background-color: #d9f1fa !important;
}
</style>
